import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';
const WEEK_FILTER_DAYS = 7;
const DEFAULT_DAYS_FILTER = 30;
const MAX_DAYS_FILTER = 365;
const DATE_FILTER_THRESHOLDS_DAYS = {
  week: WEEK_FILTER_DAYS,
  month: DEFAULT_DAYS_FILTER,
  year: MAX_DAYS_FILTER,
};

function matchesDateFilter(document, dateFilter) {
  if (dateFilter === 'all' || !document.created_at) {
    return true;
  }

  const documentDate = new Date(document.created_at);
  const now = new Date();

  if (dateFilter === 'today') {
    return documentDate.toDateString() === now.toDateString();
  }

  const thresholdDays = DATE_FILTER_THRESHOLDS_DAYS[dateFilter];
  if (!thresholdDays) {
    return true;
  }

  const thresholdDate = new Date(now.getTime() - thresholdDays * 24 * 60 * 60 * 1000);
  return documentDate >= thresholdDate;
}

function matchesDocumentFilters(document, searchQuery, typeFilter, dateFilter) {
  const normalizedQuery = searchQuery.toLowerCase();
  const matchesSearch =
    document.title.toLowerCase().includes(normalizedQuery) ||
    document.source_url?.toLowerCase().includes(normalizedQuery);
  const matchesType = typeFilter === 'all' || document.source_type === typeFilter;
  const matchesDate = matchesDateFilter(document, dateFilter);
  return matchesSearch && matchesType && matchesDate;
}

function getDeleteFailureMessage(responseData) {
  return responseData?.message || 'Erreur lors de la suppression';
}

function getEmptyStateMessage(searchQuery, typeFilter, dateFilter) {
  return searchQuery || typeFilter !== 'all' || dateFilter !== 'all'
    ? 'Aucun document ne correspond aux filtres'
    : 'Aucun document dans cette base';
}

function getNextSelectedKnowledgeBase(current, knowledgeBases) {
  return current || knowledgeBases[0] || null;
}

async function fetchKnowledgeBases(setKnowledgeBases, setSelectedKB, setLoading) {
  try {
    const response = await axios.get(`${API}/knowledge-bases`);
    setKnowledgeBases(response.data);
    setSelectedKB((current) => getNextSelectedKnowledgeBase(current, response.data));
  } catch {
    toast.error('Erreur de chargement des bases');
  } finally {
    setLoading(false);
  }
}

async function fetchDocuments(knowledgeBaseId, setDocuments) {
  try {
    const response = await axios.get(`${API}/documents/${knowledgeBaseId}`);
    setDocuments(response.data);
  } catch {
    toast.error('Erreur de chargement des documents');
  }
}

async function requestDocumentDeletion(documentId) {
  return axios.delete(`${API}/documents/${documentId}`);
}

function useDocumentsLoader() {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKB, setSelectedKB] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadKnowledgeBases = useCallback(
    () => fetchKnowledgeBases(setKnowledgeBases, setSelectedKB, setLoading),
    []
  );
  const loadDocuments = useCallback(
    (knowledgeBaseId) => fetchDocuments(knowledgeBaseId, setDocuments),
    []
  );

  useEffect(() => {
    void loadKnowledgeBases();
  }, [loadKnowledgeBases]);

  useEffect(() => {
    if (selectedKB?.id) {
      void loadDocuments(selectedKB.id);
    }
  }, [loadDocuments, selectedKB?.id]);

  return {
    knowledgeBases,
    selectedKB,
    setSelectedKB,
    documents,
    loading,
    loadKnowledgeBases,
    loadDocuments,
  };
}

function useDocumentDeletion(selectedKnowledgeBaseId, loadDocuments, loadKnowledgeBases) {
  const [deleting, setDeleting] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const refreshAfterDelete = useCallback(async () => {
    if (!selectedKnowledgeBaseId) {
      return;
    }

    await Promise.all([loadDocuments(selectedKnowledgeBaseId), loadKnowledgeBases()]);
  }, [loadDocuments, loadKnowledgeBases, selectedKnowledgeBaseId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal?.id || !selectedKnowledgeBaseId) {
      return;
    }

    const documentId = deleteModal.id;
    setDeleting(documentId);
    setDeleteModal(null);

    try {
      const response = await requestDocumentDeletion(documentId);
      if (response.data.success) {
        toast.success('Document supprimé');
        await refreshAfterDelete();
        return;
      }
      toast.error(getDeleteFailureMessage(response.data));
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  }, [deleteModal?.id, refreshAfterDelete, selectedKnowledgeBaseId]);

  return {
    deleting,
    deleteModal,
    setDeleteModal,
    handleDeleteConfirm,
  };
}

function useDocumentsFilters(documents, searchQuery, typeFilter, dateFilter) {
  const filteredDocuments = useMemo(
    () => documents.filter((document) => matchesDocumentFilters(document, searchQuery, typeFilter, dateFilter)),
    [dateFilter, documents, searchQuery, typeFilter]
  );
  const emptyStateMessage = useMemo(
    () => getEmptyStateMessage(searchQuery, typeFilter, dateFilter),
    [dateFilter, searchQuery, typeFilter]
  );

  return {
    filteredDocuments,
    emptyStateMessage,
  };
}

export function useDocumentsPageData() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const {
    knowledgeBases,
    selectedKB,
    setSelectedKB,
    documents,
    loading,
    loadKnowledgeBases,
    loadDocuments,
  } = useDocumentsLoader();
  const selectedKnowledgeBaseId = selectedKB?.id || null;
  const { deleting, deleteModal, setDeleteModal, handleDeleteConfirm } = useDocumentDeletion(
    selectedKnowledgeBaseId,
    loadDocuments,
    loadKnowledgeBases
  );
  const { filteredDocuments, emptyStateMessage } = useDocumentsFilters(
    documents,
    searchQuery,
    typeFilter,
    dateFilter
  );

  return {
    knowledgeBases,
    selectedKB,
    setSelectedKB,
    documents,
    loading,
    searchQuery,
    setSearchQuery,
    deleting,
    deleteModal,
    setDeleteModal,
    typeFilter,
    setTypeFilter,
    dateFilter,
    setDateFilter,
    filteredDocuments,
    emptyStateMessage,
    handleDeleteConfirm,
  };
}
